// src/pages/QuizResult.jsx
import { useLocation, Link } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { ArrowLeft, ArrowRight, MessageCircle, HeartHandshake } from "lucide-react";

function toArabicNum(n) {
  return n
}

export default function QuizResult() {
  const { state } = useLocation();
  const { lang, t } = useLanguage();
  const { user } = useAuth();
  const result = state?.result;

  if (!result) {
    return (
      <div className="page-content">
        <div className="container-sm text-center">
          <p>{lang === "ar" ? "لا توجد نتيجة. الرجاء إكمال الاختبار أولاً." : "No result found. Please complete the quiz first."}</p>
          <Link to="/quiz" className="btn btn-primary" style={{ marginTop: "1rem" }}>{lang === "ar" ? "ابدأ الاختبار" : "Start Quiz"}</Link>
        </div>
      </div>
    );
  }

  const { riskLevel, percentage, totalScore, maxScore } = result;

  const config = {
    high: {
      label: t("highRisk"),
      desc: t("resultHigh"),
      class: "high",
      color: "var(--color-error)"
    },
    moderate: {
      label: t("moderateRisk"),
      desc: t("resultModerate"),
      class: "moderate",
      color: "var(--color-warning)"
    },
    low: {
      label: t("lowRisk"),
      desc: t("resultLow"),
      class: "low",
      color: "var(--color-forest)"
    }
  };

  const { label, desc, class: cls } = config[riskLevel];

  const ArrowIcon = lang === "ar" ? ArrowLeft : ArrowRight;

  return (
    <div className="page-content fade-in">
      <div className="container-sm">
        <div className="card card-lg text-center" style={{ marginBottom: "1.5rem" }}>
          <h2 style={{ marginBottom: "2rem" }}>{t("resultTitle")}</h2>

          {/* Score Ring */}
          <div className={`score-ring ${cls}`}>
            <span>{lang === "ar" ? toArabicNum(percentage) : percentage}%</span>
            <span style={{ fontSize: "0.8rem", fontWeight: 500, opacity: 0.75, marginTop: "0.1rem" }}>
              {lang === "ar" ? toArabicNum(totalScore) : totalScore}/{lang === "ar" ? toArabicNum(maxScore) : maxScore}
            </span>
          </div>

          <div className={`badge badge-${cls === "high" ? "error" : cls === "moderate" ? "warn" : "green"}`} style={{ fontSize: "0.95rem", padding: "0.35rem 1.1rem", marginBottom: "1rem" }}>
            {label}
          </div>

          <p style={{ lineHeight: 1.8, maxWidth: 480, margin: "0 auto" }}>{desc}</p>

          {/* Disclaimer */}
          <div style={{ marginTop: "1.25rem", padding: "0.85rem 1rem", background: "rgba(234,179,8,0.08)", border: "1.5px solid rgba(234,179,8,0.35)", borderRadius: "var(--radius-md)", fontSize: "0.82rem", color: "var(--color-gray-600)", lineHeight: 1.7 }}>
            ⚠️ {lang === "ar" ? "هذه نتيجة أولية استرشادية فقط، ولا تُعدّ تشخيصاً طبياً. التشخيص الدقيق يتطلب تقييماً من طبيب أو أخصائي نفسي متخصص." : "This is a preliminary screening result only and does not constitute a medical diagnosis. An accurate diagnosis requires evaluation by a qualified doctor or psychologist."}
          </div>
          {/* كارت التوصيات */}
        <div className="card" style={{ marginTop: "1.5rem", borderRight: "4px solid var(--color-forest)" }}>
          <h4 style={{ marginBottom: "1rem", color: "var(--color-forest)" }}>
            {lang === "ar" ? "📋 التوصيات" : "📋 Recommendations"}
          </h4>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
            {(lang === "ar" ? [
              "🩺 استشر طبيباً أو أخصائياً نفسياً متخصصاً في ADHD للحصول على تشخيص دقيق",
              "🏫 تحدث مع معلم طفلك لمعرفة سلوكه داخل الفصل الدراسي",
              "📝 دوّن الملاحظات اليومية عن سلوك طفلك قبل موعد الطبيب",
              "💬 لا تتردد في طلب الاستشارة — التدخل المبكر يُحدث فرقاً كبيراً في حياة طفلك"
            ] : [
              "🩺 Consult a doctor or psychologist specialized in ADHD for accurate diagnosis",
              "🏫 Talk to your child's teacher about their behavior in the classroom",
              "📝 Keep daily notes about your child's behavior before the doctor's appointment",
              "💬 Don't hesitate to seek consultation — early intervention makes a big difference"
            ]).map((tip, i, arr) => (
              <div key={i} style={{
                fontSize: "0.9rem",
                lineHeight: 1.7,
                padding: "0.5rem 0",
                borderBottom: i < arr.length - 1 ? "1px solid var(--color-gray-100)" : "none"
              }}>
                {tip}
              </div>
            ))}
          </div>
        </div>

          {/* Detailed breakdown */}
          <div style={{ marginTop: "1.5rem", padding: "1rem", background: "var(--color-gray-50)", borderRadius: "var(--radius-md)", display: "inline-block" }}>
            <div style={{ fontSize: "0.85rem", color: "var(--color-gray-400)", marginBottom: "0.5rem" }}>
              {lang === "ar" ? "توزيع الإجابات" : "Answer Distribution"}
            </div>
            <div className="progress-bar-wrap" style={{ width: 280 }}>
              <div className="progress-bar-fill" style={{ width: `${percentage}%` }} />
            </div>
          </div>
        </div>

        {/* Action Cards */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
          {/* Paid */}
          <div className="card" style={{ border: "2px solid var(--color-forest)", position: "relative" }}>
            <div className="badge badge-green" style={{ position: "absolute", top: "1rem", left: lang === "ar" ? "1rem" : "auto", right: lang === "ar" ? "auto" : "1rem" }}>
              {lang === "ar" ? "موصى به" : "Recommended"}
            </div>
            <MessageCircle size={32} style={{ color: "var(--color-forest)", marginBottom: "0.75rem", marginTop: "0.5rem" }} />
            <h4 style={{ marginBottom: "0.5rem" }}>{t("paidConsult")}</h4>
            <p style={{ fontSize: "0.875rem", marginBottom: "1rem" }}>{t("paidDesc")}</p>
            <div style={{ fontWeight: 800, fontSize: "1.2rem", color: "var(--color-forest)", marginBottom: "1rem" }}>
              {lang === "ar" ? "1000 دج" : "1000 DZD"}<span style={{ fontWeight: 400, fontSize: "0.85rem", color: "var(--color-gray-400)" }}>{t("perMonth")}</span>
            </div>
            <Link
              to={user ? "/payment" : "/register?plan=paid"}
              className="btn btn-primary btn-full"
            >
              {lang === "ar" ? "اشترك الآن" : "Subscribe Now"} <ArrowIcon size={16} />
            </Link>
          </div>

          {/* Free */}
          <div className="card">
            <HeartHandshake size={32} style={{ color: "var(--color-sky-dk)", marginBottom: "0.75rem" }} />
            <h4 style={{ marginBottom: "0.5rem" }}>{t("freeConsult")}</h4>
            <p style={{ fontSize: "0.875rem", marginBottom: "1rem" }}>{t("freeDesc")}</p>
            <div style={{ fontWeight: 800, fontSize: "1.2rem", color: "var(--color-sky-dk)", marginBottom: "1rem" }}>
              {lang === "ar" ? "مجاني" : "Free"}
            </div>
            <Link
              to={user ? "/consult/free" : "/register?plan=free"}
              className="btn btn-secondary btn-full"
            >
              {lang === "ar" ? "ابدأ الاستشارة" : "Start Consultation"} <ArrowIcon size={16} />
            </Link>
          </div>
          {/* In-Person */}
          <div className="card" style={{
            gridColumn: "1 / -1",
            border: "2px solid var(--color-sky)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "1.5rem",
            flexWrap: "wrap"
          }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: "1rem", flex: 1 }}>
              <div style={{
                width: 52, height: 52, borderRadius: "var(--radius-md)",
                background: "var(--color-sky-lt)",
                display: "flex", alignItems: "center", justifyContent: "center",
                color: "var(--color-sky-dk)", flexShrink: 0, fontSize: "1.5rem"
              }}>
                🏥
              </div>
              <div>
                <h4 style={{ marginBottom: "0.4rem" }}>
                  {lang === "ar" ? "معاينة حضورية عند الطبيب" : "In-Person Doctor Visit"}
                </h4>
                <p style={{ margin: 0, fontSize: "0.875rem", color: "var(--color-gray-500)", lineHeight: 1.7 }}>
                  {lang === "ar"
                    ? "احجز موعداً مباشراً في عيادتنا بغرداية للحصول على تشخيص دقيق وخطة علاجية متكاملة وجهاً لوجه مع الطبيب المتخصص."
                    : "Book a direct appointment at our clinic in Ghardaia for an accurate diagnosis and comprehensive treatment plan face-to-face with a specialist."}
                </p>
              </div>
            </div>
            
            <a
              href="https://www.google.com/maps/search/غرداية+الجزائر"
              target="_blank"
              rel="noreferrer"
              className="btn btn-secondary"
              style={{ flexShrink: 0, display: "flex", alignItems: "center", gap: "0.5rem" }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                <circle cx="12" cy="10" r="3"/>
              </svg>
              {lang === "ar" ? "عرض الموقع على الخريطة" : "View Location on Map"}
            </a>
          </div>
        </div>



        <div className="text-center" style={{ marginTop: "1.5rem" }}>
          <Link to="/quiz" className="btn btn-ghost btn-sm">
            {lang === "ar" ? "← إعادة الاختبار" : "← Retake Quiz"}
          </Link>
        </div>
      </div>
    </div>
  );
}
